import { toast } from 'sonner'
import { create } from 'zustand'

import { getNotebooklmJobs, getNotebooklmLinks, type NotebooklmLink } from '@/api/api'
import { registerSyncListener } from '@/api/sync-events'

/** 進捗表示に必要な最小限のジョブ状態 */
export type NotebooklmJobProgress = {
  id: number
  productId: number
  productTitle: string | null
  status: 'pending' | 'running' | 'completed' | 'failed'
  progress: number
  processedSources: number
  totalSources: number
  message: string
}

type NotebooklmStore = {
  /** product_id をキーにした進行中ジョブ */
  jobs: Map<number, NotebooklmJobProgress>
  /** product_id をキーにしたノートブック URL */
  links: Map<number, string>
  /** links を一度だけ取得する（管理者のみ呼ばれる） */
  ensureLinksLoaded: () => Promise<void>
  /** 実行中ジョブを一度だけ取得する（リロード直後でも進捗を表示するため） */
  ensureActiveJobsLoaded: () => Promise<void>
  upsertJob: (job: NotebooklmJobProgress) => void
  clearJob: (productId: number) => void
  setLink: (productId: number, url: string) => void
  removeLink: (productId: number) => void
}

// 多重取得を防ぐためのフラグ（ストア外に置いて再レンダリングを起こさない）
let linksPromise: Promise<void> | null = null
let activeJobsPromise: Promise<void> | null = null

const toLinkMap = (links: NotebooklmLink[]): Map<number, string> =>
  new Map(links.map((link) => [link.product_id, link.notebook_url]))

export const useNotebooklmStore = create<NotebooklmStore>((set) => ({
  jobs: new Map(),
  links: new Map(),

  ensureLinksLoaded: () => {
    if (linksPromise) return linksPromise
    linksPromise = getNotebooklmLinks()
      .then((links) => {
        set({ links: toLinkMap(links) })
      })
      .catch((error: unknown) => {
        console.error('Failed to load NotebookLM links:', error)
        // 次回のマウントで再取得できるようにリセットする
        linksPromise = null
      })
    return linksPromise
  },

  ensureActiveJobsLoaded: () => {
    if (activeJobsPromise) return activeJobsPromise
    activeJobsPromise = getNotebooklmJobs()
      .then((jobs) => {
        const active = jobs.filter((job) => job.status === 'pending' || job.status === 'running')
        if (active.length === 0) return
        set((state) => {
          const next = new Map(state.jobs)
          for (const job of active) {
            // SSE で受け取り済みの新しい状態は上書きしない
            if (next.has(job.product_id)) continue
            next.set(job.product_id, {
              id: job.id,
              productId: job.product_id,
              productTitle: job.product_title,
              status: job.status,
              progress: job.progress,
              processedSources: job.processed_sources,
              totalSources: job.total_sources,
              message: job.message,
            })
          }
          return { jobs: next }
        })
      })
      .catch((error: unknown) => {
        console.error('Failed to load NotebookLM jobs:', error)
        activeJobsPromise = null
      })
    return activeJobsPromise
  },

  upsertJob: (job) =>
    set((state) => {
      const next = new Map(state.jobs)
      next.set(job.productId, job)
      return { jobs: next }
    }),

  clearJob: (productId) =>
    set((state) => {
      if (!state.jobs.has(productId)) return state
      const next = new Map(state.jobs)
      next.delete(productId)
      return { jobs: next }
    }),

  setLink: (productId, url) =>
    set((state) => {
      const next = new Map(state.links)
      next.set(productId, url)
      return { links: next }
    }),

  removeLink: (productId) =>
    set((state) => {
      if (!state.links.has(productId)) return state
      const next = new Map(state.links)
      next.delete(productId)
      return { links: next }
    }),
}))

// ============================================================================
// SSE リモートイベント購読
// ============================================================================
// ワーカーはファイル単位で進捗を配信する。完了・失敗はトーストで知らせ、
// 一覧に残り続けないよう少し置いてから状態を破棄する。
const CLEAR_DELAY_MS = 5_000

registerSyncListener((event) => {
  if (event.type !== 'notebooklm.updated') return

  const store = useNotebooklmStore.getState()
  const previous = store.jobs.get(event.product_id)
  const status = (event.status ?? previous?.status ?? 'pending') as NotebooklmJobProgress['status']

  store.upsertJob({
    id: event.id,
    productId: event.product_id,
    productTitle: event.product_title ?? previous?.productTitle ?? null,
    status,
    progress: event.progress ?? previous?.progress ?? 0,
    processedSources: event.processed_sources ?? previous?.processedSources ?? 0,
    totalSources: event.total_sources ?? previous?.totalSources ?? 0,
    message: event.message ?? previous?.message ?? '',
  })

  if (event.notebook_url) {
    store.setLink(event.product_id, event.notebook_url)
  }

  if (status === 'completed') {
    toast.success('NotebookLM への取り込みが完了しました')
  } else if (status === 'failed') {
    if (event.unlinked) store.removeLink(event.product_id)
    toast.error(event.message ?? 'NotebookLM への取り込みに失敗しました')
  } else {
    return
  }

  setTimeout(() => {
    useNotebooklmStore.getState().clearJob(event.product_id)
  }, CLEAR_DELAY_MS)
})
